import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, BellOffIcon, BellRingIcon, Loader2Icon, SmartphoneIcon } from 'lucide-react';
import { useAdminData } from '../../contexts/AdminDataContext';
import { defaultPrefs, savePrefs, subscribePrefs } from '../../services/adminPrefsService';
import type { NotificationPrefs } from '../../services/adminPrefsService';
import { checkPushStatus, enablePush } from '../../services/pushService';
import type { PushResult, PushStatus } from '../../services/pushService';
import { debugErrorMessage, logFirebaseError } from '../../lib/firebase';
const options: {
  key: keyof NotificationPrefs;
  label: string;
  text: string;
}[] = [{
  key: 'hoursReminder',
  label: 'Husk at registrere timer',
  text: 'Én påmindelse kl. 15.30, hvis du har en aktiv sag i dag uden registrerede timer.'
}, {
  key: 'timerReminder',
  label: 'Påmind mig når en timer stadig kører',
  text: 'Efter 2 timer og derefter hver 2. time. Timeren stoppes aldrig automatisk.'
}];
const statusInfo: Record<Exclude<PushStatus, 'checking'>, {
  title: string;
  text: string;
  icon: typeof BellRingIcon;
  tone: string;
}> = {
  active: {
    title: 'Push-notifikationer er aktive',
    text: 'Denne enhed modtager påmindelser.',
    icon: BellRingIcon,
    tone: 'text-status-ok'
  },
  'permission-missing': {
    title: 'Notifikationstilladelse mangler',
    text: 'Slå en påmindelse til for at give tilladelse. Er den afvist, skal den tillades i enhedens indstillinger. Påmindelserne vises stadig inde i appen.',
    icon: BellOffIcon,
    tone: 'text-status-warn'
  },
  unsupported: {
    title: 'Push understøttes ikke på denne enhed',
    text: 'Påmindelserne vises i stedet inde i appen — timerbjælken og "Kræver opmærksomhed" på I dag.',
    icon: AlertCircleIcon,
    tone: 'text-ink-muted'
  },
  'ios-install': {
    title: 'Appen skal installeres på hjemmeskærmen for push på iPhone',
    text: 'Åbn admin i Safari → Del → Føj til hjemmeskærm, og slå påmindelserne til inde i appen (iOS 16.4 eller nyere).',
    icon: SmartphoneIcon,
    tone: 'text-status-warn'
  }
};
function statusFromResult(result: PushResult): PushStatus {
  switch (result) {
    case 'ok':
      return 'active';
    case 'ios-not-installed':
      return 'ios-install';
    case 'unsupported':
    case 'no-service-worker':
      return 'unsupported';
    default:
      return 'permission-missing';
  }
}
export function NotificationsSection() {
  const {
    user
  } = useAdminData();
  const [prefs, setPrefs] = useState<NotificationPrefs>(defaultPrefs);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<keyof NotificationPrefs | null>(null);
  const [status, setStatus] = useState<PushStatus>('checking');
  const [error, setError] = useState('');
  useEffect(() => {
    if (!user) return;
    return subscribePrefs(user.uid, next => {
      setPrefs(next);
      setLoading(false);
    }, caught => {
      logFirebaseError('onSnapshot adminPrefs', caught);
      setLoading(false);
      setError(debugErrorMessage('adminPrefs', caught));
    });
  }, [user]);

  // Status tjekkes uden at spørge om tilladelse; et eksisterende token fornyes stille.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    checkPushStatus(user.uid).then(next => {
      if (!cancelled) setStatus(next);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);
  async function toggle(key: keyof NotificationPrefs) {
    if (!user || busy) return;
    const next = !prefs[key];
    setBusy(key);
    setError('');
    try {
      await savePrefs(user.uid, {
        [key]: next
      });
      if (next) {
        // Tilladelse bedes der først om her — når admin selv slår det til.
        setStatus(statusFromResult(await enablePush(user.uid)));
      }
    } catch (caught) {
      logFirebaseError('savePrefs', caught);
      setError(debugErrorMessage('savePrefs', caught));
    }
    setBusy(null);
  }
  const info = status === 'checking' ? null : statusInfo[status];
  return <section className="mt-5 max-w-2xl rounded-card border border-line bg-white p-6" aria-labelledby="notifikationer-titel" data-id="element-263" data-mp="23lzgwhz0a4fa">
      <h2 id="notifikationer-titel" className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-264" data-mp="1tn1ylws6b74q">
        Notifikationer
      </h2>
      <p className="mt-1.5 text-sm text-ink-muted" data-id="element-265" data-mp="1yl3fqc61gwdk">Kun påmindelser om timer. Valgene følger din bruger.</p>

      <div role="status" aria-live="polite" className="mt-4 flex items-start gap-3 rounded-xl border border-line px-4 py-3" data-id="element-266" data-mp="1ktybsxvn1hxf">
        {info ? <>
            <info.icon className={`mt-0.5 h-5 w-5 shrink-0 ${info.tone}`} aria-hidden="true" data-id="element-267" data-mp="1awskppn0xqx7" />
            <div className="min-w-0" data-id="element-268" data-mp="1fvn52w0n3njf">
              <p className="text-[15px] font-medium text-ink" data-id="element-269" data-mp="1fwon5kg2paan">{info.title}</p>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-270" data-mp="11vccnojom0w5">{info.text}</p>
            </div>
          </> : <p className="flex items-center gap-2 text-sm text-ink-muted" data-id="element-271" data-mp="15weco7lfagz6">
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-272" data-mp="n8ncdkhlvjop" />
            Tjekker push på denne enhed …
          </p>}
      </div>

      <ul className="mt-2 divide-y divide-line" data-id="element-273" data-mp="ibwwoy9pqq3t">
        {options.map(option => <li key={option.key} className="flex items-center justify-between gap-4 py-3.5" data-id="element-274" data-mp="wbeprpu1kpiz">
            <div className="min-w-0" data-id="element-275" data-mp="r9z7g5j90x4a">
              <p id={`notif-${option.key}`} className="text-[15px] font-medium text-ink" data-id="element-276" data-mp="rd3gbg7nsh1a">
                {option.label}
              </p>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-277" data-mp="119q001o9121q">{option.text}</p>
            </div>
            <button type="button" role="switch" aria-checked={prefs[option.key]} aria-labelledby={`notif-${option.key}`} disabled={loading || busy !== null} onClick={() => toggle(option.key)} className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 ease-smooth disabled:opacity-60 ${prefs[option.key] ? 'bg-accent' : 'bg-canvas-deep'}`} data-id="element-278" data-mp="1f0l1uu0byivt">
              <span className={`inline-block h-5 w-5 rounded-full bg-ink shadow transition-transform duration-200 ease-smooth ${prefs[option.key] ? 'translate-x-6' : 'translate-x-1'}`} aria-hidden="true" data-id="element-279" data-mp="lo4t8p2tik1y" />
            </button>
          </li>)}
      </ul>

      {error && <p role="alert" className="mt-3 flex items-start gap-2 text-sm text-danger" data-id="element-280" data-mp="15b2rrs4mqyo2">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-281" data-mp="19dfq3vh1ruat" />
          {error}
        </p>}
    </section>;
}